import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isUserPremium } from '../../services/tracker-limited-data';
import {
    Box,
    CircularProgress,
    Typography,
    Card,
    CardContent,
    Button,
    Container
} from '@mui/material';
import { Lock as LockIcon, Star as StarIcon } from '@mui/icons-material';

function PremiumRoute({ children }) {
    const { currentUser, userData, loading } = useAuth();
    const [isPremium, setIsPremium] = useState(false);
    const [checkingPremium, setCheckingPremium] = useState(true);

    useEffect(() => {
        const checkPremiumAccess = async () => {
            if (!currentUser || !userData) {
                setCheckingPremium(false);
                return;
            }

            try {
                const hasPremiumAccess = await isUserPremium(currentUser.uid);
                setIsPremium(hasPremiumAccess);
            } catch (error) {
                console.error('Błąd podczas sprawdzania uprawnień premium:', error);
                setIsPremium(false);
            } finally {
                setCheckingPremium(false);
            }
        };

        checkPremiumAccess();
    }, [currentUser, userData]);

    // Pokazuj loading podczas sprawdzania autoryzacji
    if (loading || checkingPremium) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '50vh',
                    gap: 2
                }}
            >
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                    Sprawdzanie uprawnień premium...
                </Typography>
            </Box>
        );
    }

    // Jeśli użytkownik nie jest zalogowany
    if (!currentUser) {
        return <Navigate to="/" replace />;
    }

    // Jeśli użytkownik nie ma uprawnień premium
    if (!isPremium) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Card
                    sx={{
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
                        border: '1px solid rgba(124, 58, 237, 0.3)',
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                    }}
                >
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <Box sx={{ mb: 4 }}>
                            <LockIcon
                                sx={{
                                    fontSize: 80,
                                    color: 'primary.main',
                                    mb: 2,
                                    filter: 'drop-shadow(0 4px 8px rgba(124, 58, 237, 0.3))'
                                }}
                            />
                            <StarIcon
                                sx={{
                                    fontSize: 60,
                                    color: '#ffd700',
                                    position: 'absolute',
                                    top: 20,
                                    right: 20,
                                    opacity: 0.7
                                }}
                            />
                        </Box>

                        <Typography
                            variant="h4"
                            component="h1"
                            gutterBottom
                            sx={{
                                fontWeight: 'bold',
                                background: 'linear-gradient(45deg, #7c3aed, #3b82f6)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                mb: 2
                            }}
                        >
                            Funkcja Premium
                        </Typography>

                        <Typography
                            variant="h6"
                            color="text.secondary"
                            sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}
                        >
                            Tracker jest dostępny tylko dla użytkowników z uprawnieniami premium.
                            Skontaktuj się z administratorem, aby uzyskać dostęp do tej funkcji.
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                onClick={() => window.history.back()}
                                sx={{
                                    background: 'linear-gradient(135deg, #7c3aed, #3b82f6)',
                                    borderRadius: 2,
                                    px: 4,
                                    py: 1.5,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #6d28d9, #2563eb)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 8px 25px rgba(124, 58, 237, 0.4)'
                                    }
                                }}
                            >
                                Wróć
                            </Button>

                            <Button
                                variant="outlined"
                                onClick={() => window.location.href = '/'}
                                sx={{
                                    borderColor: 'primary.main',
                                    color: 'primary.main',
                                    borderRadius: 2,
                                    px: 4,
                                    py: 1.5,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                        borderColor: 'primary.dark',
                                        backgroundColor: 'rgba(124, 58, 237, 0.1)',
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                            >
                                Strona główna
                            </Button>
                        </Box>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 4, fontStyle: 'italic' }}
                        >
                            Twoja obecna rola: <strong>{userData?.role || 'user'}</strong>
                        </Typography>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    return children;
}

export default PremiumRoute;
